import * as React from "react";
import classNames from "classnames";
import { addDays, isSameDay, differenceInDays, format } from "date-fns";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { StoreState } from "../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { translate, InjectedTranslateProps } from "react-i18next";

import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import EventNoteIcon from "@material-ui/icons/EventNote";
import { changeInquiryListDateRange } from "../../actions/store/inquiry-list-actions";
import { getInquiriesAndUpdateStore } from "../../actions/inquiry-actions";
import { ConnectedMobileMenu } from "./mobile-menu";

export interface InquiryListItemsProps {}

interface State {
	dateSelectAnchor: HTMLElement;
}

interface ConnectedInquiryListItemsProps extends WithStyles<typeof styles> {
	dateRange: {
		startDate: Date;
		endDate: Date;
	};
	layout: "desktop" | "tablet" | "phone";
	changeDateRange: (startDate: Date, endDate: Date) => void;
}

type Props = InquiryListItemsProps &
	ConnectedInquiryListItemsProps &
	InjectedTranslateProps;

type DateRange = 7 | 14 | 30 | "all";

export class InquiryListItems extends React.Component<Props, State> {
	public state: State = {
		dateSelectAnchor: null
	};

	private handleDateSelectClick = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		this.setState({ dateSelectAnchor: event.currentTarget });
	};

	private handleDateSelectClose = () => {
		this.setState({ dateSelectAnchor: null });
	};

	private handleDateRangeSelect = (range: DateRange) => {
		const startDate = new Date();
		let endDate;

		switch (range) {
			case 7:
				endDate = addDays(startDate, 7);
				break;

			case 14:
				endDate = addDays(startDate, 14);
				break;

			case 30:
				endDate = addDays(startDate, 30);
				break;

			default:
				endDate = null;
				break;
		}

		if (endDate === null) {
			this.props.changeDateRange(null, null);
		} else {
			this.props.changeDateRange(startDate, endDate);
		}

		getInquiriesAndUpdateStore(startDate, endDate);
	};

	private printDateRange = () => {
		const { startDate, endDate } = this.props.dateRange;
		const now = new Date();

		if (!startDate || !endDate) {
			return "Gesamter Zeitraum";
		}

		if (isSameDay(startDate, now)) {
			const difference = differenceInDays(endDate, startDate);

			if (difference <= 31) {
				return `Nächste ${difference} Tage`;
			} else {
				return `Heute bis ${format(endDate, "DD.MM.YY")}`;
			}
		}

		return `${format(startDate, "DD.MM.YY")} bis ${format(
			endDate,
			"DD.MM.YY"
		)}`;
	};

	private renderDateSelect = () => {
		const { classes, layout } = this.props;
		const { dateSelectAnchor } = this.state;

		return (
			<React.Fragment>
				<Button
					className={classNames(
						classes.dropdownButton,
						classes.dateSelectButton
					)}
					variant={layout === "phone" ? "text" : "outlined"}
					color="inherit"
					onClick={this.handleDateSelectClick}
				>
					<EventNoteIcon className={classes.iconSmall} />
					<span className={classes.iconButtonLabel}>
						{this.printDateRange()}
					</span>
				</Button>
				<Menu
					anchorEl={dateSelectAnchor}
					open={Boolean(dateSelectAnchor)}
					onClick={this.handleDateSelectClose}
				>
					<MenuItem onClick={() => this.handleDateRangeSelect(7)}>
						Nächste 7 Tage
					</MenuItem>
					<MenuItem onClick={() => this.handleDateRangeSelect(14)}>
						Nächste 14 Tage
					</MenuItem>
					<MenuItem onClick={() => this.handleDateRangeSelect(30)}>
						Nächste 30 Tage
					</MenuItem>
					<MenuItem onClick={() => this.handleDateRangeSelect("all")}>
						Alles anzeigen
					</MenuItem>
				</Menu>
			</React.Fragment>
		);
	};

	private renderPhone() {
		return <ConnectedMobileMenu />;
	}

	public render() {
		const { classes, layout } = this.props;
		const { dateSelectAnchor } = this.state;

		if (layout === "phone") {
			return (
				<React.Fragment>
					{this.renderDateSelect()}
					<div className={classes.flexExpand} />
					{this.renderPhone()}
				</React.Fragment>
			);
		}

		return (
			<React.Fragment>
				{this.renderDateSelect()}
				<div className={classes.flexExpand} />
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	dateRange: state.inquiryListDateRange,
	layout: state.layout
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	changeDateRange: (startDate: Date, endDate: Date) =>
		dispatch(changeInquiryListDateRange(startDate, endDate))
});

const styles = (theme: Theme) =>
	createStyles({
		iconSmall: {
			fontSize: 20
		},
		iconButtonLabel: {
			textTransform: "none",
			paddingLeft: 6,
			paddingRight: 6
		},
		dateSelectButton: {
			marginRight: 8
		},
		dropdownButton: {
			paddingLeft: 8,
			paddingRight: 8,
			borderColor: "rgba(255, 255, 255, 0.3)"
		},
		flexExpand: {
			flexGrow: 1
		}
	});

export const ConnectedInquiryListItems = translate("translations")(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(InquiryListItems)
	)
);
